import React, { useEffect, useRef, useState } from "react";
// @ts-ignore
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import audioIcon from "../../assets/audio.svg";
import chatIcon from "../../assets/chat.svg";
import checkIcon from "../../assets/check.svg";
import closeIcon from "../../assets/close.svg";
import endIcon from "../../assets/end.svg";
import ali from "../../assets/ali.svg";
import yzl from "../../assets/yzllink.svg";
import WaveformCanvas from "./components/waveform";
import { WebAudioRecognition } from "./components/webaudio-recognition";
import "./index.css";
import { apiUrl } from "../../api";
import Live2DComponent, { Live2DInstance } from "./components/live2d";

// 模型配置
const MODEL_CONFIGS = [
  {
    id: "dashscope",
    name: "DashScope",
    icon: ali,
    alt: "DashScope",
  },
  {
    id: "dify",
    name: "Dify",
    icon: yzl,
    alt: "Dify",
  },
] as const;

type ModelType = (typeof MODEL_CONFIGS)[number]["id"];

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

const Live2d: React.FC = () => {
  const width = 400;
  const height = 800;
  const digitalHumanRef = useRef<Live2DInstance>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // 数字人组件位置（右下角）
  const [position, setPosition] = useState({ right: 20, bottom: 20 });
  // 拖拽状态
  const [dragging, setDragging] = useState(false);
  // 拖拽偏移量
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // 是否显示输入栏
  const [showInputBar, setShowInputBar] = useState(false);
  // 音频队列刷新用key
  const [refreshKey, setRefreshKey] = useState(0);
  // 聊天历史记录
  const [chatHistory, setChatHistory] = useState<
    Array<{
      id?: string;
      type: "user" | "ai" | "user_live";
      content: string;
    }>
  >([]);
  // 聊天框滚动容器引用
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // 是否处于语音输入激活状态
  const [isVoiceInputActive, setIsVoiceInputActive] = useState(false);
  // 是否正在录音
  const [isRecording, setIsRecording] = useState(false);
  // 当前语音识别文本
  const [transcript, setTranscript] = useState("");
  // TTS待处理文本队列
  const pendingTextQueue = useRef<string[]>([]);
  // TTS处理状态
  const isProcessingText = useRef(false);
  // 已准备好播放的音频队列
  const readyAudioQueue = useRef<string[]>([]);
  // 是否正在播放音频
  const isPlaying = useRef(false);
  // 麦克风音频流引用
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // 音频分析器引用
  const analyserRef = useRef<AnalyserNode | null>(null);
  // 输入框引用
  const inputRef = useRef<HTMLInputElement>(null);
  // 是否处于实时对话模式
  const [liveTalking, setLiveTalking] = useState(false);
  // WebAudioRecognition实例引用
  const webaudioRecognitionRef = useRef<WebAudioRecognition | null>(null);
  // 当前选中的AI模型
  const [selectedModel, setSelectedModel] = useState<ModelType>("dashscope");
  // 是否显示模型选择器
  const [showModelSelector, setShowModelSelector] = useState(false);
  // 当前语音识别模式（webaudio/browser）
  const [speechRecognitionMode, setSpeechRecognitionMode] = useState<
    "webaudio" | "browser"
  >("webaudio");
  // 会话ID
  const sessionIdRef = useRef("");
  // 浏览器SpeechRecognition实例引用
  const speechRecognitionRef = useRef<any>(null);
  const randomIdRef = useRef("");
  // 是否是第一次返回音频
  const firstAudioReturnedRef = useRef(false);
  // 待处理完整内容
  const pendingFullContentRef = useRef("");

  // 初始化 WebAudio 语音识别
  useEffect(() => {
    sessionIdRef.current = Math.random().toString(36).substring(2, 15);
    if (speechRecognitionMode === "webaudio") {
      initAudioRecognition();
    } else {
      initBrowserRecognition();
    }
  }, []);

  // 录音结束后自动填充输入框内容
  useEffect(() => {
    if (
      !isVoiceInputActive &&
      transcript.replace(/\s+/g, "") &&
      inputRef.current
    ) {
      inputRef.current.value = transcript.replace(/\s+/g, "");
      inputRef.current.focus();
    }
  }, [isVoiceInputActive, transcript]);

  // 拖拽事件监听（合并解绑逻辑）
  useEffect(() => {
    const move = (e: MouseEvent) => handleMouseMove(e);
    const up = () => handleMouseUp();
    if (dragging) {
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    }
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging, offset]);

  // 聊天记录更新时自动滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // 音频队列刷新时自动播放下一个
  useEffect(() => {
    playNextReadyAudio();
  }, [refreshKey]);

  // 组件卸载时的清理工作
  useEffect(() => {
    return () => {
      // 清理WebAudioRecognition
      if (webaudioRecognitionRef.current) {
        webaudioRecognitionRef.current.disconnect();
      }

      // 清理浏览器SpeechRecognition
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }

      // 清理媒体流
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // 清理音频上下文
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
    };
  }, []);

  // 初始化WebAudio识别
  const initAudioRecognition = () => {
    webaudioRecognitionRef.current = new WebAudioRecognition(
      (text: string, isFinal: boolean) => {
        setTranscript(text);
        if (isFinal && text.replace(/\s+/g, "")) {
          console.log("WebAudio 识别结果:", text);
        }
      },
      (error: string) => {
        console.error("WebAudio 识别错误:", error);
        alert(error);
      },
      () => {
        console.log("录音已取消");
      },
    );
  };

  // 初始化浏览器识别
  const initBrowserRecognition = () => {
    // 兼容不同浏览器的SpeechRecognition对象
    const SpeechRecognition =
      window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("当前浏览器不支持语音识别功能");
      return;
    }
    speechRecognitionRef.current = new SpeechRecognition();
    speechRecognitionRef.current.lang = "zh-CN";
    speechRecognitionRef.current.continuous = true;
    speechRecognitionRef.current.interimResults = true;
    speechRecognitionRef.current.onresult = (event: any) => {
      const result = event.results[event.results.length - 1];
      const text = result[0].transcript;
      setTranscript(text);
    };
    speechRecognitionRef.current.onend = () => {
      setIsRecording(false);
    };
  };

  // 语音输入结束（确认或取消）
  const handleVoiceInputEnd = (isConfirm: boolean) => {
    if (isConfirm) {
      stopRecording();
    } else {
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
      // 取消录音，不发送请求
      if (webaudioRecognitionRef.current) {
        webaudioRecognitionRef.current.cancelRecording();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      setIsRecording(false);
    }
    setIsVoiceInputActive(false);
    if (!isConfirm) setTranscript("");
  };

  // 滚动聊天框到底部
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  // 鼠标按下，开始拖拽
  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setOffset({
        x: rect.right - e.clientX,
        y: rect.bottom - e.clientY,
      });
    }
    document.body.style.userSelect = "none";
  };

  // 鼠标移动，更新位置
  const handleMouseMove = (e: MouseEvent) => {
    if (dragging) {
      const right = window.innerWidth - e.clientX - offset.x;
      const bottom = window.innerHeight - e.clientY - offset.y;
      setPosition({ right, bottom });
    }
  };

  // 鼠标松开，结束拖拽
  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = "";
  };

  // 输入框回车发送消息
  const handleInputEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = (e.target as HTMLInputElement).value.replace(/\s+/g, "");
      if (value) {
        updateHistory(value, "user");
        callOpenAI(value);
        (e.target as HTMLInputElement).value = "";
      }
    }
  };

  // 清理Markdown格式，用于TTS
  const cleanMarkdownForTTS = (text: string): string => {
    return (
      text
        // 移除标题符号
        .replace(/^#{1,6}\s+/gm, "")
        // 移除粗体符号
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/__(.*?)__/g, "$1")
        // 移除斜体符号
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/_(.*?)_/g, "$1")
        // 移除删除线
        .replace(/~~(.*?)~~/g, "$1")
        // 移除行内代码
        .replace(/`([^`]+)`/g, "$1")
        // 移除代码块
        .replace(/```[\s\S]*?```/g, "")
        // 移除引用符号
        .replace(/^>\s+/gm, "")
        // 移除列表符号
        .replace(/^[\s]*[-*+]\s+/gm, "")
        .replace(/^[\s]*\d+\.\s+/gm, "")
        // 移除链接，保留文本
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // 移除图片
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
        // 移除分割线
        .replace(/^---+$/gm, "")
        // 移除表格符号
        .replace(/\|/g, " ")
        .replace(/^[\s]*\|[\s]*$/gm, "")
        // 清理多余的空行
        .replace(/\n\s*\n/g, "\n")
        // 清理首尾空白
        .replace(/\s+/g, "")
    );
  };

  // 移除表情符号
  const removeEmojis = (text: string) => {
    return text
      .replace(/[\u{1F600}-\u{1F64F}]/gu, "")
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, "")
      .replace(/[\u{1F680}-\u{1F6FF}]/gu, "")
      .replace(/[\u{2600}-\u{26FF}]/gu, "")
      .replace(/[\u{2700}-\u{27BF}]/gu, "")
      .replace(/[\u{1F900}-\u{1F9FF}]/gu, "")
      .replace(/[\u{1FA70}-\u{1FAFF}]/gu, "")
      .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, "");
  };

  // 添加文本到TTS队列
  const addTextToQueue = (text: string) => {
    let cleanText = cleanMarkdownForTTS(text);
    cleanText = removeEmojis(cleanText);
    if (cleanText.replace(/\s+/g, "")) {
      pendingTextQueue.current.push(cleanText);
      if (!isProcessingText.current) processTextQueue();
    }
  };

  // 按顺序处理TTS文本队列（串行，保证语音顺序）
  const processTextQueue = async () => {
    if (isProcessingText.current || pendingTextQueue.current.length === 0)
      return;
    isProcessingText.current = true;
    while (pendingTextQueue.current.length > 0) {
      const text = pendingTextQueue.current.shift()!;
      try {
        const res = await axios.post(`${apiUrl}/api/tts`, {
          text,
          tts_engine: "index",
        });

        const audioUrl = `${apiUrl}${res.data.url}`;
        firstAudioReturnedRef.current = true;
        setChatHistory((prev) => {
          const newHistory = [...prev];
          let index = newHistory.findIndex(
            (item) => item.id === randomIdRef.current,
          );
          if (index !== -1) {
            newHistory[index].content = pendingFullContentRef.current;
          }
          return newHistory;
        });
        readyAudioQueue.current.push(audioUrl);
        if (!isPlaying.current) setRefreshKey((prev) => prev + 1);
      } catch (error: any) {
        console.error("❌ TTS请求失败:", error);
        // 如果第一句就失败了，将文本显示出来，不卡在思考界面
        if (!firstAudioReturnedRef.current) {
          firstAudioReturnedRef.current = true;
          setChatHistory((prev) => {
            const newHistory = [...prev];
            let index = newHistory.findIndex(
              (item) => item.id === randomIdRef.current,
            );
            if (index !== -1) {
              newHistory[index].content = pendingFullContentRef.current;
            }
            return newHistory;
          });
        }
      }
    }
    isProcessingText.current = false;
  };

  // 播放下一个音频
  const playNextReadyAudio = () => {
    if (isPlaying.current || readyAudioQueue.current.length === 0) return;
    const audioUrl = readyAudioQueue.current.shift();
    if (!audioUrl) return;

    isPlaying.current = true;

    try {
      digitalHumanRef.current?.stopMotions();
      digitalHumanRef.current?.playMotion(audioUrl, {
        onFinish: () => {
          console.log("✅ 音频播放完成");
          isPlaying.current = false;
          setRefreshKey((prev) => prev + 1);
        },
        onError: (error: any) => {
          console.error("❌ 音频播放失败:", error);
          isPlaying.current = false;
          setRefreshKey((prev) => prev + 1);
        },
      });
    } catch (error: any) {
      console.error("❌ 播放音频时发生错误:", error);
      isPlaying.current = false;
      setRefreshKey((prev) => prev + 1);
    }
  };

  // 分句条件判断与分割逻辑
  const splitSentenceIfNeeded = (
    currentSentence: string,
    addTextToQueue: (text: string) => void,
  ): string => {
    const END_PUNCTUATION = /[。！？；：,!?;:，]/;
    if (
      currentSentence.length > 0 &&
      END_PUNCTUATION.test(currentSentence[currentSentence.length - 1])
    ) {
      // 末尾是结尾符，直接分句
      addTextToQueue(currentSentence.replace(/\s+/g, ""));
      return "";
    } else if (currentSentence.length >= 20) {
      // 超过20字但末尾不是结尾符
      // 从后往前找最近的结尾符
      let lastPuncIndex = -1;
      for (let i = currentSentence.length - 1; i >= 0; i--) {
        if (END_PUNCTUATION.test(currentSentence[i])) {
          lastPuncIndex = i;
          break;
        }
      }
      if (lastPuncIndex !== -1) {
        // 找到结尾符，分割
        const sentenceToQueue = currentSentence
          .slice(0, lastPuncIndex + 1)
          .replace(/\s+/g, "");
        addTextToQueue(sentenceToQueue);
        // 剩余部分继续累积
        return currentSentence.slice(lastPuncIndex + 1);
      } else {
        // 没有结尾符，整句入队
        addTextToQueue(currentSentence.replace(/\s+/g, ""));
        return "";
      }
    }
    return currentSentence;
  };

  // 调用OpenAI接口获取AI回复
  const callOpenAI = async (prompt: string) => {
    const url = `${apiUrl}/api/chat`;
    resetQueue();
    firstAudioReturnedRef.current = false;
    pendingFullContentRef.current = "";
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: prompt,
          type: selectedModel,
          session_id: sessionIdRef.current,
        }),
      });
      if (!response.body) throw new Error("无响应流");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let currentSentence = "";
      let randomId = Math.random().toString(36).substring(2, 15);
      randomIdRef.current = randomId;
      setChatHistory((prev) => {
        return [
          ...prev,
          { type: "ai", content: "正在思考中...", id: randomId },
        ];
      });
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (currentSentence.replace(/\s+/g, ""))
            addTextToQueue(currentSentence.replace(/\s+/g, ""));
          break;
        }
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const dataStr = line.slice(6);
          if (dataStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(dataStr);
            if (parsed.content) {
              fullContent += parsed.content;
              currentSentence += parsed.content;
              pendingFullContentRef.current = fullContent;
              // 实时更新聊天历史
              if (firstAudioReturnedRef.current) {
                setChatHistory((prev) => {
                  const newHistory = [...prev];
                  let index = newHistory.findIndex(
                    (item) => item.id === randomId,
                  );
                  if (index !== -1) {
                    newHistory[index].content = fullContent;
                  }
                  return newHistory;
                });
              }
              // 分句
              currentSentence = splitSentenceIfNeeded(
                currentSentence,
                addTextToQueue,
              );
            }
          } catch (e) {
            console.error("解析流式数据失败:", e, dataStr);
          }
        }
        if (fullContent === "") {
          setChatHistory((prev) => {
            const newHistory = [...prev];
            let index = newHistory.findIndex((item) => item.id === randomId);
            if (index !== -1 && newHistory[index].content === "正在思考中...") {
              newHistory[index].content = "请求出错，请重试";
            }
            return newHistory;
          });
        }
      }
    } catch (err) {
      console.error("请求失败:", err);
      setChatHistory((prev) => {
        const newHistory = [...prev];
        newHistory[newHistory.length - 1] = {
          type: "ai",
          content: "抱歉，请求出错了",
          id: "400",
        };
        return newHistory;
      });
    }
  };

  // 重置动画
  const resetQueue = () => {
    pendingTextQueue.current = [];
    readyAudioQueue.current = [];
    isProcessingText.current = false;
    isPlaying.current = false;
    digitalHumanRef.current?.stopMotions();
  };

  // 语音输入按钮点击，控制录音状态
  const handleVoiceInputClick = () => {
    setIsVoiceInputActive(!isVoiceInputActive);
    if (!isVoiceInputActive) {
      startRecording();
    } else {
      stopRecording();
    }
  };

  // 开始录音，初始化音频流和分析器
  const startRecording = async () => {
    // 检查语音识别初始化
    if (
      (speechRecognitionMode === "webaudio" &&
        !webaudioRecognitionRef.current) ||
      (speechRecognitionMode === "browser" && !speechRecognitionRef.current)
    ) {
      console.error("❌ 语音识别未初始化");
      return;
    }

    // 检查HTTPS
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      console.error("❌ 非HTTPS环境，手机浏览器可能无法访问麦克风");
      return;
    }

    try {
      console.log("🎵 请求麦克风权限...");

      // 针对手机浏览器的音频配置
      const audioConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
          // 手机浏览器特殊配置
          ...(navigator.userAgent.includes("Mobile") && {
            sampleRate: { ideal: 16000, min: 8000, max: 48000 },
            channelCount: { ideal: 1, min: 1, max: 2 },
          }),
        },
      };

      const stream =
        await navigator.mediaDevices.getUserMedia(audioConstraints);

      mediaStreamRef.current = stream;

      // 创建AudioContext（针对手机浏览器的特殊处理）
      let audioContext;
      try {
        audioContext = new (
          window.AudioContext || (window as any).webkitAudioContext
        )({
          sampleRate: 16000,
          latencyHint: "interactive",
        });
        console.log("✅ AudioContext创建成功");
      } catch (audioError) {
        console.warn("⚠️ AudioContext创建失败，尝试备用方案:", audioError);
        audioContext = new (
          window.AudioContext || (window as any).webkitAudioContext
        )();
      }

      // 检查AudioContext状态
      if (audioContext.state === "suspended") {
        console.log("⏸️ AudioContext被暂停，尝试恢复...");
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      source.connect(analyser);
      analyser.fftSize = 256;

      console.log("✅ 音频分析器设置完成");
    } catch (error) {
      console.error("❌ 获取麦克风权限失败:", error);

      // 详细的错误信息
      let errorMessage = "无法访问麦克风";
      if (error instanceof DOMException) {
        switch (error.name) {
          case "NotAllowedError":
            errorMessage = "麦克风权限被拒绝，请在浏览器设置中允许麦克风访问";
            break;
          case "NotFoundError":
            errorMessage = "未找到麦克风设备";
            break;
          case "NotSupportedError":
            errorMessage = "当前浏览器不支持麦克风功能";
            break;
          case "NotReadableError":
            errorMessage = "麦克风被其他应用占用，请关闭其他录音应用";
            break;
          default:
            errorMessage = `麦克风访问失败: ${error.message}`;
        }
      }

      alert(errorMessage);
      return;
    }

    setIsRecording((prev) => {
      console.log("📊 录音状态更新:", prev, "->", true);
      return true;
    });
    setTranscript("");

    try {
      if (speechRecognitionMode === "webaudio") {
        console.log("🎵 使用WebAudio模式");
        initAudioRecognition();
        await webaudioRecognitionRef.current?.startRecording();
      } else {
        console.log("🌐 使用浏览器识别模式");
        initBrowserRecognition();
        speechRecognitionRef.current.start();
      }
    } catch (recognitionError) {
      console.error("❌ 语音识别启动失败:", recognitionError);
      alert("语音识别启动失败，请重试");
      stopRecording();
    }
  };

  // 停止录音，关闭音频流
  const stopRecording = () => {
    if (webaudioRecognitionRef.current && isRecording) {
      webaudioRecognitionRef.current.stopRecording();
    }
    if (speechRecognitionRef.current && isRecording) {
      speechRecognitionRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setIsRecording(false);
  };

  // 实时对话：开启实时语音识别
  const startLiveTalking = async () => {
    setLiveTalking(true);
    if (
      (speechRecognitionMode === "webaudio" &&
        !webaudioRecognitionRef.current) ||
      (speechRecognitionMode === "browser" && !speechRecognitionRef.current)
    ) {
      alert("语音识别未初始化");
      setLiveTalking(false);
      return;
    }
    try {
      new AudioContext();
    } catch (error) {
      alert("无法访问麦克风，请检查权限设置");
      setLiveTalking(false);
      return;
    }
    if (speechRecognitionMode === "webaudio") {
      await startAudioRecognition();
    } else {
      startBrowserRecognition();
    }
  };

  // 开始WebAudio识别
  const startAudioRecognition = async () => {
    // 使用 WebAudio 进行实时对话
    webaudioRecognitionRef.current = new WebAudioRecognition(
      (text: string, isFinal: boolean) => {
        if (!text.replace(/\s+/g, "")) return;
        updateHistory(text.replace(/\s+/g, ""), "user");
        if (isFinal) callOpenAI(text.replace(/\s+/g, ""));
      },
      (error: string) => {
        console.error("实时对话识别错误:", error);
      },
      () => {
        console.log("实时对话录音已取消");
      },
    );

    try {
      await webaudioRecognitionRef.current.startRecording();
    } catch {
      stopLiveTalking();
    }
  };

  // 开始浏览器识别
  const startBrowserRecognition = () => {
    speechRecognitionRef.current.onresult = (event: any) => {
      const result = event.results[event.results.length - 1];
      const text = result[0].transcript;
      if (!text.replace(/\s+/g, "")) return;
      updateHistory(text.replace(/\s+/g, ""), "user");
      if (result.isFinal) callOpenAI(text.replace(/\s+/g, ""));
    };
    speechRecognitionRef.current.onend = () => {
      startBrowserRecognition();
    };
    speechRecognitionRef.current.start();
  };

  // 更新聊天历史
  const updateHistory = (text: string, type: "ai" | "user") => {
    // 实时显示 user_live
    setChatHistory((prev) => {
      const newHistory = [...prev];
      if (
        newHistory.length > 0 &&
        newHistory[newHistory.length - 1].type === type
      ) {
        newHistory[newHistory.length - 1] = { type: type, content: text };
      } else {
        newHistory.push({ type: type, content: text });
      }
      return newHistory;
    });
  };

  // 停止实时对话，点击"停止对话"时调用
  const stopLiveTalking = () => {
    setLiveTalking(false);
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.onend = null;
      speechRecognitionRef.current.stop();
    }
    if (webaudioRecognitionRef.current) {
      webaudioRecognitionRef.current.disconnect();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setChatHistory((prev) => prev.filter((msg) => msg.type !== "user_live"));
    resetQueue();
  };

  // 模型选择处理函数
  const handleModelSelect = (model: ModelType) => {
    setSelectedModel(model);
    setShowModelSelector(false);
  };

  // 切换模型选择器显示状态
  const toggleModelSelector = () => {
    setShowModelSelector(!showModelSelector);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <div
        ref={containerRef}
        className="live2d-container"
        onMouseDown={handleMouseDown}
        style={{
          right: position.right,
          bottom: position.bottom,
          width: width,
          height: height,
          cursor: dragging ? "grabbing" : "grab",
        }}
      >
        <div onClick={() => setShowInputBar((prev) => !prev)}>
          <Live2DComponent
            ref={digitalHumanRef}
            width={width}
            height={height}
          />
        </div>
      </div>
      {showInputBar && (
        <div className="live2d-chat-panel">
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
            className="live2d-chat-history"
            ref={chatContainerRef}
          >
            {chatHistory.map((message, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: ["user", "user_live"].includes(message.type)
                    ? "flex-end"
                    : "flex-start",
                  marginBottom: "8px",
                  padding: "8px",
                }}
              >
                <div
                  className="live2d-chat-message"
                  style={{
                    background: message.type === "user" ? "#007AFF" : "#F0F0F0",
                    color: message.type === "user" ? "white" : "black",
                    ...(message.content === "正在思考中..." && {
                      background: "#E8E8E8",
                      color: "#666",
                      fontStyle: "italic",
                    }),
                    ...(message.type === "user_live" && {
                      background: "#e6f7ff",
                      color: "#1890ff",
                      fontStyle: "italic",
                    }),
                  }}
                >
                  {message.content === "正在思考中..." ? (
                    <div className="live2d-loading">
                      <span className="live2d-loading-spinner"></span>
                      <span style={{ marginLeft: 8 }}>正在思考中...</span>
                    </div>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showInputBar && (
        <div
          className={`live2d-input-bar${liveTalking ? " live2d-input-bar-end" : ""}`}
        >
          {!liveTalking && (
            <div className="live2d-input-bar-content">
              <div style={{ flex: 1, width: 1, height: 32 }}>
                {isRecording ? (
                  <WaveformCanvas analyser={analyserRef.current} />
                ) : (
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="询问任何问题"
                    onKeyDown={handleInputEnter}
                    className="live2d-input"
                  />
                )}
              </div>
              <button
                className="live2d-btn live2d-btn-audio"
                style={{ display: isVoiceInputActive ? "none" : "block" }}
                title="语音输入"
                onClick={handleVoiceInputClick}
              >
                <img
                  src={audioIcon}
                  alt="语音输入"
                  style={{ width: 20, height: 20 }}
                />
              </button>
              <button
                className="live2d-btn live2d-btn-chat"
                style={{ display: isVoiceInputActive ? "none" : "block" }}
                title="实时对话开关"
                onClick={startLiveTalking}
              >
                <img
                  src={chatIcon}
                  alt="实时对话"
                  style={{ width: 16, height: 16 }}
                />
              </button>
              <button
                className="live2d-btn live2d-btn-model"
                style={{ display: isVoiceInputActive ? "none" : "block" }}
                title="模型选择"
                onClick={toggleModelSelector}
              >
                <img
                  src={
                    MODEL_CONFIGS.find((config) => config.id === selectedModel)
                      ?.icon || ali
                  }
                  alt="模型选择"
                  style={{ width: 16, height: 16 }}
                />
              </button>
              {isVoiceInputActive && (
                <>
                  <button
                    className="live2d-btn live2d-btn-check"
                    title="确认"
                    onClick={() => handleVoiceInputEnd(true)}
                  >
                    <img
                      src={checkIcon}
                      alt="确认"
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
                  <button
                    className="live2d-btn live2d-btn-close"
                    title="取消"
                    onClick={() => handleVoiceInputEnd(false)}
                  >
                    <img
                      src={closeIcon}
                      alt="取消"
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
                </>
              )}
            </div>
          )}
          {liveTalking && (
            <img
              src={endIcon}
              alt="停止对话"
              style={{
                width: 24,
                height: 24,
                color: "#f5222d",
                cursor: "pointer",
              }}
              onClick={stopLiveTalking}
            />
          )}
          {showModelSelector && (
            <div
              className="live2d-model-selector"
              style={{
                height: `${MODEL_CONFIGS.length * 24 + 20}px`,
              }}
            >
              {MODEL_CONFIGS.map((config) => (
                <button
                  key={config.id}
                  className={`live2d-model-option ${selectedModel === config.id ? "live2d-model-option-active" : ""}`}
                  onClick={() => handleModelSelect(config.id)}
                  title={config.name}
                >
                  <img
                    src={config.icon}
                    alt={config.alt}
                    style={{ width: 20, height: 20 }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Live2d;
